import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { scaleQuantity } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ShareButtons } from '../components/ShareButtons';
import { CommentsSection, StarRating } from '../components/CommentsSection';
import { IngredientSubstitutionModal } from '../components/IngredientSubstitutionModal';
import { 
    Clock, 
    Users, 
    ChefHat, 
    Heart,
    ArrowLeft,
    ArrowRight,
    Printer,
    Star,
    PiggyBank,
    Loader2,
    Lock,
    ExternalLink,
    Link as LinkIcon,
    Minus,
    Plus,
    Edit3,
    Crown,
    ListOrdered,
    ShoppingCart,
    Snowflake
} from 'lucide-react';
import { toast } from 'sonner';

export const RecipeDetailPage = () => {
    const { id } = useParams();
    const { user, isPremium } = useAuth();
    const navigate = useNavigate();
    
    const [recipe, setRecipe] = useState(null);
    const [isFavorite, setIsFavorite] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [recipesUsingThis, setRecipesUsingThis] = useState([]);
    const [selectedServings, setSelectedServings] = useState(null);
    const [currentPack, setCurrentPack] = useState(null);
    const [substitutionModal, setSubstitutionModal] = useState({ open: false, ingredient: null });
    const [userSubstitutions, setUserSubstitutions] = useState([]);

    useEffect(() => {
        loadRecipe();
        loadCurrentPack();
    }, [id]);

    useEffect(() => {
        // Initialize selectedServings when recipe loads
        if (recipe && selectedServings === null) {
            setSelectedServings(recipe.servings);
        }
    }, [recipe]);

    useEffect(() => {
        // Load user substitutions when pack is loaded
        if (currentPack?.id && isPremium) {
            loadUserSubstitutions();
        }
    }, [currentPack, isPremium]);

    const loadCurrentPack = async () => {
        try {
            const res = await api.getWeeklyPack();
            setCurrentPack(res.data);
        } catch (e) {
            // No current pack available
        }
    };

    const loadUserSubstitutions = async () => {
        try {
            const res = await api.getUserSubstitutions(currentPack.id);
            setUserSubstitutions(res.data.substitutions || []);
        } catch (e) {
            // No substitutions
        }
    };

    // Get substitution for a specific ingredient
    const getSubstitutionForIngredient = (ingredientName) => {
        const normalized = ingredientName.toLowerCase().trim();
        return userSubstitutions.find(s => 
            s.original.toLowerCase().trim() === normalized ||
            normalized.includes(s.original.toLowerCase().trim()) ||
            s.original.toLowerCase().trim().includes(normalized)
        );
    };

    const openSubstitutionModal = (ingredient) => {
        if (!isPremium) {
            toast.error('Fonctionnalité Premium requise');
            navigate('/subscription');
            return;
        }
        if (!currentPack) {
            toast.error('Aucun pack hebdomadaire actif');
            return;
        }
        setSubstitutionModal({ open: true, ingredient });
    };

    const handleSubstitutionApplied = () => {
        toast.success('Modification appliquée à votre liste d\'épicerie!');
        // Reload substitutions to show changes
        if (currentPack?.id) {
            loadUserSubstitutions();
        }
    };

    const loadRecipe = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.getRecipe(id);
            setRecipe(response.data);
            
            // Load recipes that use this one as an ingredient
            try {
                const usedInResponse = await api.getRecipesUsingThis(id);
                setRecipesUsingThis(usedInResponse.data);
            } catch (e) {
                console.log('No recipes using this');
            }
            
            if (user) {
                const favResponse = await api.checkFavorite(id);
                setIsFavorite(favResponse.data.is_favorite);
            }
        } catch (error) {
            console.error('Error loading recipe:', error);
            if (error.response?.status === 403) {
                setError('premium');
            } else if (error.response?.status === 404) {
                setError('not_found');
            } else {
                setError('unknown');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleFavoriteToggle = async () => {
        if (!user) {
            navigate('/login');
            return;
        }
        
        try {
            if (isFavorite) {
                await api.removeFavorite(id);
                setIsFavorite(false);
                toast.success('Retiré des favoris');
            } else {
                await api.addFavorite(id);
                setIsFavorite(true);
                toast.success('Ajouté aux favoris');
            }
        } catch (error) {
            toast.error('Erreur lors de la mise à jour des favoris');
        }
    };

    const handlePrint = () => {
        window.print();
    };

    // Calculate ratio for ingredient scaling
    const servingsRatio = selectedServings && recipe ? selectedServings / recipe.servings : 1;

    // Local wrapper to scale quantity with current ratio
    const scaleIngredientQuantity = (quantity) => {
        return scaleQuantity(quantity, servingsRatio);
    };

    // Scale cost based on ratio
    const scaleCost = (cost) => {
        const num = parseFloat(cost);
        if (isNaN(num)) return 0;
        return num * servingsRatio;
    };

    const calculateTotalCost = () => {
        return recipe.ingredients.reduce((sum, ing) => sum + scaleCost(ing.estimated_cost), 0);
    };

    const handleServingsChange = (delta) => {
        const newServings = Math.max(1, Math.min(20, selectedServings + delta));
        setSelectedServings(newServings);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (error === 'premium') {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center max-w-md mx-auto px-4">
                    <div className="w-20 h-20 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Lock className="w-10 h-10 text-accent" />
                    </div>
                    <h1 className="font-heading text-2xl font-bold text-foreground mb-4">
                        Recette Premium
                    </h1>
                    <p className="text-muted-foreground mb-6">
                        Cette recette est réservée aux membres Premium. 
                        Débloquez l&apos;accès à toutes nos recettes exclusives!
                    </p>
                    <div className="flex gap-3 justify-center">
                        <Button variant="outline" onClick={() => navigate(-1)} className="rounded-full">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Retour
                        </Button>
                        <Button 
                            onClick={() => navigate('/subscription')}
                            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                        >
                            Devenir Premium
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    if (error === 'not_found' || !recipe) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="font-heading text-2xl font-bold text-foreground mb-4">
                        Recette non trouvée
                    </h1>
                    <Button variant="outline" onClick={() => navigate('/recipes')} className="rounded-full">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Retour aux recettes
                    </Button>
                </div>
            </div>
        );
    }

    const totalTime = recipe.prep_time + recipe.cook_time;
    const totalCost = calculateTotalCost();

    return (
        <div className="min-h-screen py-8 md:py-12 print:py-4" data-testid="recipe-detail-page">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Back Button */}
                <Button
                    variant="ghost"
                    onClick={() => navigate(-1)}
                    className="mb-6 print:hidden"
                    data-testid="back-btn"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour
                </Button>

                {/* Hero Image */}
                <div className="relative rounded-3xl overflow-hidden mb-8 aspect-video">
                    <img
                        src={recipe.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=1200'}
                        alt={recipe.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    
                    {recipe.is_premium && (
                        <div className="absolute top-4 left-4">
                            <span className="premium-badge flex items-center gap-1 text-sm">
                                <Star className="w-4 h-4" />
                                Premium
                            </span>
                        </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="absolute top-4 right-4 flex gap-2 print:hidden">
                        <ShareButtons 
                            title={recipe.title}
                            description={recipe.description}
                            variant="popup"
                        />
                        <Button
                            size="icon"
                            variant="secondary"
                            onClick={handlePrint}
                            className="rounded-full bg-white/90 hover:bg-white"
                            data-testid="print-btn"
                        >
                            <Printer className="w-4 h-4" />
                        </Button>
                        <Button
                            size="icon"
                            onClick={handleFavoriteToggle}
                            className={`rounded-full ${
                                isFavorite 
                                    ? 'bg-primary text-white hover:bg-primary/90' 
                                    : 'bg-white/90 hover:bg-white text-foreground'
                            }`}
                            data-testid="favorite-btn"
                        >
                            <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
                        </Button>
                    </div>
                    
                    {/* Cost Badge */}
                    <div className="absolute bottom-4 left-4">
                        <span className="price-tag text-lg">
                            {recipe.cost_per_serving.toFixed(2)}$/portion
                        </span>
                    </div>
                </div>

                {/* Title & Meta */}
                <div className="mb-8">
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        {(recipe.categories || [recipe.category]).map(cat => (
                            <Badge key={cat} variant="secondary" className="capitalize">
                                {cat === 'breakfast' && 'Déjeuner'}
                                {cat === 'lunch' && 'Dîner'}
                                {cat === 'dinner' && 'Souper'}
                                {cat === 'soups' && 'Soupes'}
                                {cat === 'appetizers' && 'Entrées'}
                                {cat === 'salads' && 'Salades'}
                                {cat === 'sides' && 'Accompagnements'}
                                {cat === 'dessert' && 'Dessert'}
                                {cat === 'snack' && 'Collation'}
                                {cat === 'base' && 'Les bases'}
                                {cat === 'basics' && 'Les bases'}
                                {cat === 'rescue' && 'Recette secours'}
                                {cat === 'airfryer' && 'Friteuse à air'}
                                {cat === 'slowcooker' && 'Mijoteuse'}
                                {cat === 'pressurecooker' && 'Auto-cuiseur'}
                                {cat === 'bbq' && 'BBQ'}
                            </Badge>
                        ))}
                        <Badge variant="outline" className="capitalize">
                            {recipe.difficulty}
                        </Badge>
                        {recipe.protein_type && (
                            <Badge className="bg-red-100 text-red-700 border-red-200">
                                {recipe.protein_type === 'poulet' && '🍗 Poulet'}
                                {recipe.protein_type === 'boeuf' && '🥩 Bœuf'}
                                {recipe.protein_type === 'porc' && '🥓 Porc'}
                                {recipe.protein_type === 'poisson' && '🐟 Poisson'}
                                {recipe.protein_type === 'vegetarien' && '🥗 Végétarien'}
                            </Badge>
                        )}
                        {recipe.freezable && (
                            <Badge className="bg-cyan-100 text-cyan-700 border-cyan-200">
                                <Snowflake className="w-3 h-3 mr-1" />
                                Se congèle bien
                            </Badge>
                        )}
                        {recipe.tags.map(tag => (
                            <Badge key={tag} variant="outline" className="text-muted-foreground">
                                {tag}
                            </Badge>
                        ))}
                    </div>
                    
                    <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
                        {recipe.title}
                    </h1>
                    
                    <p className="text-lg text-muted-foreground mb-6">
                        {recipe.description}
                    </p>
                    
                    {/* Stats */}
                    <div className="flex flex-wrap gap-6 text-muted-foreground">
                        <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5 text-primary" />
                            <span>
                                <strong className="text-foreground">{totalTime}</strong> min total
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Users className="w-5 h-5 text-primary" />
                            <span>
                                <strong className="text-foreground">{recipe.servings}</strong> portions (original)
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <PiggyBank className="w-5 h-5 text-secondary" />
                            <span>
                                <strong className="text-foreground">{totalCost.toFixed(2)}$</strong> total
                            </span>
                        </div>
                        {recipe.average_rating > 0 && (
                            <div className="flex items-center gap-2">
                                <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                                <span>
                                    <strong className="text-foreground">{recipe.average_rating}</strong>/5
                                    <span className="text-sm ml-1">({recipe.rating_count} avis)</span>
                                </span>
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <Heart className="w-5 h-5 text-primary" />
                            <span>
                                <strong className="text-foreground">{recipe.likes_count}</strong> favoris
                            </span>
                        </div>
                    </div>
                </div>

                {/* Servings Selector */}
                <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-6 mb-8 print:hidden">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <Users className="w-6 h-6 text-primary" />
                            <span className="font-heading font-bold text-lg text-foreground">
                                Ajuster les portions
                            </span>
                        </div>
                        <div className="flex items-center gap-4">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleServingsChange(-1)}
                                disabled={selectedServings <= 1}
                                className="rounded-full h-10 w-10 border-2"
                            >
                                <Minus className="w-5 h-5" />
                            </Button>
                            <div className="text-center min-w-[80px]">
                                <span className="font-heading font-bold text-3xl text-primary">
                                    {selectedServings}
                                </span>
                                <p className="text-sm text-muted-foreground">
                                    portion{selectedServings > 1 ? 's' : ''}
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleServingsChange(1)}
                                disabled={selectedServings >= 20}
                                className="rounded-full h-10 w-10 border-2"
                            >
                                <Plus className="w-5 h-5" />
                            </Button>
                        </div>
                        {selectedServings !== recipe.servings && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedServings(recipe.servings)}
                                className="text-muted-foreground hover:text-foreground"
                            >
                                Réinitialiser ({recipe.servings})
                            </Button>
                        )}
                    </div>
                    {selectedServings !== recipe.servings && (
                        <p className="text-sm text-muted-foreground text-center mt-3">
                            Les quantités et coûts sont ajustés pour <strong>{selectedServings}</strong> portion{selectedServings > 1 ? 's' : ''} 
                            (au lieu de {recipe.servings})
                        </p>
                    )}
                </div>

                {/* Prep/Cook Time */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                    <div className="bg-muted/50 rounded-2xl p-4 text-center">
                        <p className="text-sm text-muted-foreground mb-1">Préparation</p>
                        <p className="font-heading font-bold text-2xl text-foreground">{recipe.prep_time} min</p>
                    </div>
                    <div className="bg-muted/50 rounded-2xl p-4 text-center">
                        <p className="text-sm text-muted-foreground mb-1">Cuisson</p>
                        <p className="font-heading font-bold text-2xl text-foreground">{recipe.cook_time} min</p>
                    </div>
                </div>

                {/* Ingredients & Steps Tabs - Side by Side on Desktop */}
                <Tabs defaultValue="ingredients" className="mb-8">
                    <TabsList className="grid grid-cols-2 w-full max-w-md mx-auto mb-6">
                        <TabsTrigger value="ingredients" className="flex items-center gap-2 font-bold" data-testid="tab-ingredients">
                            <ShoppingCart className="w-4 h-4" />
                            Ingrédients
                        </TabsTrigger>
                        <TabsTrigger value="steps" className="flex items-center gap-2 font-bold" data-testid="tab-steps">
                            <ListOrdered className="w-4 h-4" />
                            Préparation
                        </TabsTrigger>
                    </TabsList>
                    
                    {/* Desktop: Side by Side View */}
                    <div className="hidden lg:grid lg:grid-cols-2 lg:gap-6">
                        {/* Ingredients Column */}
                        <div className="bg-card rounded-2xl border border-border p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="font-heading text-xl font-bold text-foreground flex items-center gap-2">
                                    <ShoppingCart className="w-5 h-5 text-primary" />
                                    Ingrédients
                                </h2>
                                <div className="flex items-center gap-2">
                                    {isPremium && currentPack && (
                                        <Badge className="bg-gradient-to-r from-orange-500 to-amber-500 text-white border-0 text-xs">
                                            <Crown className="w-3 h-3 mr-1" />
                                            Modifiable
                                        </Badge>
                                    )}
                                    {selectedServings !== recipe.servings && (
                                        <Badge className="bg-primary/10 text-primary border-primary/20 text-xs">
                                            {selectedServings} portions
                                        </Badge>
                                    )}
                                </div>
                            </div>
                            
                            <ul className="space-y-2">
                                {recipe.ingredients.map((ing, index) => {
                                    const substitution = getSubstitutionForIngredient(ing.name);
                                    const isExcluded = substitution?.action === 'exclude';
                                    const isReplaced = substitution?.action === 'replace';
                                    const isModified = substitution?.action === 'modify_quantity';
                                    
                                    return (
                                    <li 
                                        key={index}
                                        className={`flex items-center justify-between py-2 border-b border-border/50 last:border-0 group ${isExcluded ? 'opacity-50 bg-red-50 rounded-lg px-2 -mx-2' : ''}`}
                                    >
                                        <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                                            <span className={`text-foreground text-sm ${isExcluded ? 'line-through text-gray-400' : ''}`}>
                                                <strong className={`${selectedServings !== recipe.servings ? 'text-primary' : ''} ${isModified ? 'text-blue-600' : ''}`}>
                                                    {isModified ? substitution.new_quantity : `${scaleIngredientQuantity(ing.quantity)} ${ing.unit}`}
                                                </strong>{' '}
                                                {ing.linked_recipe_id ? (
                                                    <Link 
                                                        to={`/recipes/${ing.linked_recipe_id}`}
                                                        className="text-purple-600 hover:text-purple-800 underline decoration-2 decoration-purple-300 hover:decoration-purple-500 transition-colors font-medium"
                                                    >
                                                        {ing.name}
                                                        <ExternalLink className="w-3 h-3 inline ml-1 -mt-0.5" />
                                                    </Link>
                                                ) : (
                                                    ing.name
                                                )}
                                            </span>
                                            
                                            {isReplaced && (
                                                <span className="flex items-center gap-1 text-green-600 font-medium text-xs">
                                                    <ArrowRight className="w-3 h-3" />
                                                    <span className="bg-green-100 px-2 py-0.5 rounded-full">
                                                        {substitution.new_ingredient}
                                                    </span>
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <span className={`font-medium text-sm ${selectedServings !== recipe.servings ? 'text-primary' : 'text-secondary'} ${isExcluded ? 'line-through' : ''}`}>
                                                {scaleCost(ing.estimated_cost).toFixed(2)}$
                                            </span>
                                            {user && (
                                                <button
                                                    onClick={() => openSubstitutionModal({
                                                        name: ing.name,
                                                        quantity: `${scaleIngredientQuantity(ing.quantity)} ${ing.unit || ''}`.trim()
                                                    })}
                                                    className={`p-1 rounded-lg transition-all ${
                                                        isPremium 
                                                            ? 'bg-primary/10 hover:bg-primary/20 text-primary opacity-0 group-hover:opacity-100' 
                                                            : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-0 group-hover:opacity-50'
                                                    }`}
                                                    title={isPremium ? "Modifier" : "Premium"}
                                                >
                                                    <Edit3 className="w-3 h-3" />
                                                </button>
                                            )}
                                        </div>
                                    </li>
                                    );
                                })}
                            </ul>
                            
                            <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                                <span className="font-medium text-foreground text-sm">Coût total:</span>
                                <span className={`font-heading font-bold text-lg ${selectedServings !== recipe.servings ? 'text-primary' : 'text-secondary'}`}>
                                    {totalCost.toFixed(2)}$
                                </span>
                            </div>
                        </div>
                        
                        {/* Steps Column */}
                        <div className="bg-card rounded-2xl border border-border p-6">
                            <h2 className="font-heading text-xl font-bold text-foreground mb-6 flex items-center gap-2">
                                <ListOrdered className="w-5 h-5 text-primary" />
                                Étapes de préparation
                            </h2>
                            
                            <ol className="space-y-4">
                                {recipe.steps.map((step, index) => (
                                    <li key={index} className="flex gap-3">
                                        <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                                            <span className="font-heading font-bold text-primary text-sm">
                                                {index + 1}
                                            </span>
                                        </div>
                                        <p className="text-foreground leading-relaxed pt-1 text-sm">
                                            {step}
                                        </p>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>

                    {/* Mobile: Tab Content */}
                    <div className="lg:hidden">
                        <TabsContent value="ingredients">
                            <div className="bg-card rounded-2xl border border-border p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="font-heading text-2xl font-bold text-foreground flex items-center gap-2">
                                        <ChefHat className="w-6 h-6 text-primary" />
                                        Ingrédients
                                    </h2>
                                    <div className="flex items-center gap-2">
                                        {isPremium && currentPack && (
                                            <Badge className="bg-gradient-to-r from-orange-500 to-amber-500 text-white border-0 text-xs">
                                                <Crown className="w-3 h-3 mr-1" />
                                                Modifiable
                                            </Badge>
                                        )}
                                        {selectedServings !== recipe.servings && (
                                            <Badge className="bg-primary/10 text-primary border-primary/20">
                                                Ajusté pour {selectedServings} portion{selectedServings > 1 ? 's' : ''}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                                
                                <ul className="space-y-3">
                                    {recipe.ingredients.map((ing, index) => {
                                        const substitution = getSubstitutionForIngredient(ing.name);
                                        const isExcluded = substitution?.action === 'exclude';
                                        const isReplaced = substitution?.action === 'replace';
                                        const isModified = substitution?.action === 'modify_quantity';
                                        
                                        return (
                                        <li 
                                            key={index}
                                            className={`flex items-center justify-between py-2 border-b border-border/50 last:border-0 group ${isExcluded ? 'opacity-50 bg-red-50 rounded-lg px-2 -mx-2' : ''}`}
                                        >
                                            <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                                                <span className={`text-foreground ${isExcluded ? 'line-through text-gray-400' : ''}`}>
                                                    <strong className={`${selectedServings !== recipe.servings ? 'text-primary' : ''} ${isModified ? 'text-blue-600' : ''}`}>
                                                        {isModified ? substitution.new_quantity : `${scaleIngredientQuantity(ing.quantity)} ${ing.unit}`}
                                                    </strong>{' '}
                                                    {ing.linked_recipe_id ? (
                                                        <Link 
                                                            to={`/recipes/${ing.linked_recipe_id}`}
                                                            className="text-purple-600 hover:text-purple-800 underline decoration-2 decoration-purple-300 hover:decoration-purple-500 transition-colors font-medium"
                                                            data-testid={`ingredient-link-${index}`}
                                                        >
                                                            {ing.name}
                                                            <ExternalLink className="w-3.5 h-3.5 inline ml-1 -mt-0.5" />
                                                        </Link>
                                                    ) : (
                                                        ing.name
                                                    )}
                                                </span>
                                                
                                                {isReplaced && (
                                                    <span className="flex items-center gap-1 text-green-600 font-medium">
                                                        <ArrowRight className="w-4 h-4" />
                                                        <span className="bg-green-100 px-2 py-0.5 rounded-full text-sm">
                                                            {substitution.new_ingredient}
                                                            {substitution.new_quantity && ` (${substitution.new_quantity})`}
                                                        </span>
                                                    </span>
                                                )}
                                                
                                                {isExcluded && (
                                                    <Badge variant="outline" className="text-xs bg-red-100 text-red-600 border-red-200">
                                                        Exclu
                                                    </Badge>
                                                )}
                                                {isModified && (
                                                    <Badge variant="outline" className="text-xs bg-blue-100 text-blue-600 border-blue-200">
                                                        Modifié
                                                    </Badge>
                                                )}
                                                {isReplaced && (
                                                    <Badge variant="outline" className="text-xs bg-green-100 text-green-600 border-green-200">
                                                        Remplacé
                                                    </Badge>
                                                )}
                                                
                                                {ing.linked_recipe_id && (
                                                    <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                                        Recette maison
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className={`font-medium ${selectedServings !== recipe.servings ? 'text-primary' : 'text-secondary'} ${isExcluded ? 'line-through' : ''}`}>
                                                    {scaleCost(ing.estimated_cost).toFixed(2)}$
                                                </span>
                                                {user && (
                                                    <button
                                                        onClick={() => openSubstitutionModal({
                                                            name: ing.name,
                                                            quantity: `${scaleIngredientQuantity(ing.quantity)} ${ing.unit || ''}`.trim()
                                                        })}
                                                        className={`p-2 rounded-lg transition-all ${
                                                            isPremium 
                                                                ? 'bg-primary/10 hover:bg-primary/20 text-primary' 
                                                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                        }`}
                                                        title={isPremium ? "Modifier cet ingrédient" : "Fonctionnalité Premium"}
                                                        data-testid={`edit-ingredient-${index}`}
                                                    >
                                                        <Edit3 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </li>
                                        );
                                    })}
                                </ul>
                                
                                {user && !isPremium && (
                                    <div className="mt-4 p-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200">
                                        <p className="text-sm text-orange-700 flex items-center gap-2">
                                            <Crown className="w-4 h-4" />
                                            <span><strong>Premium</strong> : Modifiez les ingrédients et mettez à jour votre liste d&apos;épicerie automatiquement!</span>
                                        </p>
                                    </div>
                                )}
                                
                                <div className="mt-6 pt-4 border-t border-border flex items-center justify-between">
                                    <span className="font-medium text-foreground">
                                        Coût total estimé {selectedServings !== recipe.servings && `(${selectedServings} portions)`}:
                                    </span>
                                    <span className={`font-heading font-bold text-xl ${selectedServings !== recipe.servings ? 'text-primary' : 'text-secondary'}`}>
                                        {totalCost.toFixed(2)}$
                                    </span>
                                </div>
                                
                                {selectedServings !== recipe.servings && (
                                    <p className="text-sm text-muted-foreground mt-2 text-right">
                                        {(totalCost / selectedServings).toFixed(2)}$/portion
                                    </p>
                                )}
                            </div>
                        </TabsContent>
                        
                        <TabsContent value="steps">
                            <div className="bg-card rounded-2xl border border-border p-6">
                                <h2 className="font-heading text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
                                    <ListOrdered className="w-6 h-6 text-primary" />
                                    Étapes de préparation
                                </h2>
                                
                                <ol className="space-y-6">
                                    {recipe.steps.map((step, index) => (
                                        <li key={index} className="flex gap-4">
                                            <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                                <span className="font-heading font-bold text-primary">
                                                    {index + 1}
                                                </span>
                                            </div>
                                            <p className="text-foreground leading-relaxed pt-2">
                                                {step}
                                            </p>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </TabsContent>
                    </div>
                </Tabs>

                {/* Tip/Astuce Bubble */}
                {recipe.tip && (
                    <div className="mb-8">
                        <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-2xl border-2 border-yellow-300 p-5 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-24 h-24 bg-yellow-200/30 rounded-full -translate-y-1/2 translate-x-1/2" />
                            <div className="absolute bottom-0 left-0 w-16 h-16 bg-amber-200/30 rounded-full translate-y-1/2 -translate-x-1/2" />
                            <div className="relative flex gap-4 items-start">
                                <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                                    <span className="text-2xl">💡</span>
                                </div>
                                <div>
                                    <h3 className="font-heading font-bold text-lg text-amber-800 mb-1">
                                        Astuce du chef
                                    </h3>
                                    <p className="text-amber-900 leading-relaxed">
                                        {recipe.tip}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Recipe Variants Section */}
                {recipe.variants && recipe.variants.length > 0 && (
                    <div className="mb-8" data-testid="recipe-variants-section">
                        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-3xl border-2 border-purple-200 p-6 md:p-8">
                            <h2 className="font-heading text-2xl font-bold text-foreground mb-2 flex items-center gap-3">
                                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl flex items-center justify-center shadow-lg">
                                    <span className="text-2xl">🎨</span>
                                </div>
                                Variantes
                            </h2>
                            <p className="text-muted-foreground mb-6">
                                {recipe.variants.length} variante{recipe.variants.length > 1 ? 's' : ''} disponible{recipe.variants.length > 1 ? 's' : ''} pour cette recette
                            </p>
                            
                            <div className="grid gap-4">
                                {recipe.variants.map((variant, vIndex) => (
                                    <div 
                                        key={vIndex}
                                        className="bg-white rounded-2xl p-5 border-2 border-purple-100 hover:border-purple-300 transition-colors"
                                        data-testid={`variant-${vIndex}`}
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                                variant.variant_type === 'topping' ? 'bg-orange-100' :
                                                variant.variant_type === 'alternative' ? 'bg-green-100' :
                                                'bg-blue-100'
                                            }`}>
                                                <span className="text-xl">
                                                    {variant.variant_type === 'topping' ? '🍳' :
                                                     variant.variant_type === 'alternative' ? '🔄' : '💡'}
                                                </span>
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                    <h3 className="font-heading font-bold text-lg text-foreground">
                                                        {variant.name}
                                                    </h3>
                                                    <Badge variant="outline" className={`text-xs ${
                                                        variant.variant_type === 'topping' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                                                        variant.variant_type === 'alternative' ? 'bg-green-50 text-green-700 border-green-200' :
                                                        'bg-blue-50 text-blue-700 border-blue-200'
                                                    }`}>
                                                        {variant.variant_type === 'topping' ? 'Garniture' :
                                                         variant.variant_type === 'alternative' ? 'Alternative' : 'Suggestion'}
                                                    </Badge>
                                                    {variant.extra_cost > 0 && (
                                                        <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-xs">
                                                            +{variant.extra_cost.toFixed(2)}$
                                                        </Badge>
                                                    )}
                                                </div>
                                                
                                                {variant.description && (
                                                    <p className="text-muted-foreground text-sm mb-3">
                                                        {variant.description}
                                                    </p>
                                                )}
                                                
                                                {/* Variant Ingredients */}
                                                {variant.ingredients && variant.ingredients.length > 0 && (
                                                    <div className="mb-3">
                                                        <p className="text-xs font-medium text-purple-600 mb-2">Ingrédients :</p>
                                                        <div className="flex flex-wrap gap-2">
                                                            {variant.ingredients.map((ing, iIndex) => (
                                                                <span 
                                                                    key={iIndex}
                                                                    className="inline-flex items-center gap-1 bg-purple-50 text-purple-800 px-3 py-1 rounded-full text-sm"
                                                                >
                                                                    <span className="font-medium">{ing.quantity}</span>
                                                                    {ing.unit && <span>{ing.unit}</span>}
                                                                    <span>{ing.name}</span>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Variant Instructions */}
                                                {variant.instructions && (
                                                    <div className="bg-purple-50 rounded-xl p-3 border border-purple-100">
                                                        <p className="text-xs font-medium text-purple-600 mb-1">Instructions :</p>
                                                        <p className="text-sm text-purple-900">{variant.instructions}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Cuisiner en solo Section */}
                {recipe.solo_tips && recipe.solo_tips.length > 0 && (
                    <div className="mb-8">
                        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl border-2 border-orange-200 p-6 md:p-8">
                            <h2 className="font-heading text-2xl font-bold text-foreground mb-4 flex items-center gap-3">
                                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-2xl flex items-center justify-center shadow-lg">
                                    <span className="text-2xl">👤</span>
                                </div>
                                Cuisiner en solo
                            </h2>
                            <p className="text-muted-foreground mb-6">
                                Conseils et astuces pour adapter cette recette quand on cuisine pour soi
                            </p>
                            
                            <div className="space-y-4">
                                {recipe.solo_tips.map((tip, index) => (
                                    <div 
                                        key={index}
                                        className="flex gap-4 bg-white rounded-2xl p-4 shadow-sm border border-orange-100"
                                    >
                                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                                            <span className="text-lg">💡</span>
                                        </div>
                                        <p className="text-foreground leading-relaxed">{tip}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Source/Credit */}
                {recipe.source_name && (
                    <div className="mb-8">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-4 flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                <span className="text-xl">📚</span>
                            </div>
                            <div>
                                <p className="text-sm text-blue-600">Recette inspirée de</p>
                                {recipe.source_url ? (
                                    <a 
                                        href={recipe.source_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-medium text-blue-800 hover:text-blue-600 hover:underline flex items-center gap-1"
                                    >
                                        {recipe.source_name}
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </a>
                                ) : (
                                    <p className="font-medium text-blue-800">{recipe.source_name}</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Share Section */}
                <div className="mb-8 print:hidden">
                    <div className="bg-gradient-to-r from-green-50 via-purple-50 to-orange-50 rounded-3xl border p-6 text-center">
                        <h3 className="font-heading font-bold text-lg mb-2">Vous avez aimé cette recette?</h3>
                        <p className="text-muted-foreground mb-4">Partagez-la avec vos amis et votre famille!</p>
                        <ShareButtons 
                            title={recipe.title}
                            description={recipe.description}
                            variant="inline"
                            size="lg"
                        />
                    </div>
                </div>

                {/* Recipes using this one as ingredient */}
                {recipesUsingThis.length > 0 && (
                    <div className="mb-8">
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl border-2 border-purple-200 p-6 md:p-8">
                            <h2 className="font-heading text-2xl font-bold text-foreground mb-2 flex items-center gap-3">
                                <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
                                    <LinkIcon className="w-5 h-5 text-white" />
                                </div>
                                Recettes utilisant cet ingrédient
                            </h2>
                            <p className="text-muted-foreground mb-6">
                                Cette recette est utilisée comme ingrédient dans {recipesUsingThis.length} autre{recipesUsingThis.length > 1 ? 's' : ''} recette{recipesUsingThis.length > 1 ? 's' : ''}
                            </p>
                            
                            <div className="grid gap-4">
                                {recipesUsingThis.map((linkedRecipe) => (
                                    <Link 
                                        key={linkedRecipe.id}
                                        to={`/recipes/${linkedRecipe.id}`}
                                        className="flex items-center gap-4 bg-white rounded-2xl p-4 border-2 border-purple-100 hover:border-purple-400 hover:shadow-lg hover:-translate-y-1 transition-all group"
                                        data-testid={`linked-recipe-${linkedRecipe.id}`}
                                    >
                                        <img 
                                            src={linkedRecipe.image_url || 'https://images.pexels.com/photos/4122953/pexels-photo-4122953.jpeg?w=200'}
                                            alt={linkedRecipe.title}
                                            className="w-20 h-20 rounded-xl object-cover border-2 border-purple-100"
                                        />
                                        <div className="flex-1">
                                            <h3 className="font-heading font-bold text-lg text-foreground group-hover:text-purple-600 transition-colors">
                                                {linkedRecipe.title}
                                            </h3>
                                            <p className="text-sm text-muted-foreground line-clamp-1">
                                                {linkedRecipe.description}
                                            </p>
                                            <div className="flex items-center gap-3 mt-2">
                                                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-bold">
                                                    {linkedRecipe.cost_per_serving?.toFixed(2)}$/portion
                                                </span>
                                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                                                    {linkedRecipe.prep_time + linkedRecipe.cook_time} min
                                                </span>
                                            </div>
                                        </div>
                                        <ArrowRight className="w-5 h-5 text-purple-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all" />
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Author */}
                <div className="bg-muted/30 rounded-2xl p-6 text-center mb-8 print:hidden">
                    <p className="text-sm text-muted-foreground mb-1">Recette partagée par</p>
                    <p className="font-heading font-bold text-lg text-foreground">
                        {recipe.author_name}
                    </p>
                </div>

                {/* Comments Section */}
                <div className="print:hidden">
                    <CommentsSection recipeId={id} />
                </div>
            </div>

            {/* Ingredient Substitution Modal */}
            <IngredientSubstitutionModal
                isOpen={substitutionModal.open}
                onClose={() => setSubstitutionModal({ open: false, ingredient: null })}
                ingredient={substitutionModal.ingredient}
                recipeId={id}
                packId={currentPack?.id}
                onSubstitutionApplied={handleSubstitutionApplied}
            />
        </div>
    );
};

export default RecipeDetailPage;
